const STRATEGY = Object.freeze({
  CONCAT: "concat",
  ALTERNATE: "alternate"
});

/**
 * Builds queue item payload used by transport requests.
 * @param {Object} series
 * @param {string|number} imageId
 * @returns {{key:string,studyId:string|number,seriesId:string|number,imageId:string|number}}
 */
const toDownloadItem = (series, imageId) => ({
  key: series.key,
  studyId: series.studyId,
  seriesId: series.seriesId,
  imageId
});

/**
 * Normalizes image identifiers to stable string keys.
 * @param {string|number} imageId
 * @returns {string}
 */
const toImageKey = imageId => String(imageId);

/**
 * Concatenates incoming queues preserving per-series item order.
 * @param {Array} addingQueue
 * @param {Array} downloadQueue
 * @returns {Array}
 */
const concatStrategy = (addingQueue, downloadQueue) => {
  const nextQueue = downloadQueue.slice();
  addingQueue.forEach(series => {
    series.imageIds.forEach(imageId => {
      nextQueue.push(toDownloadItem(series, imageId));
    });
  });
  return nextQueue;
};

/**
 * Merges queues with round-robin strategy across series keys.
 * @param {Array} addingQueue
 * @param {Array} downloadQueue
 * @returns {Array}
 */
const alternateStrategy = (addingQueue, downloadQueue) => {
  const keys = [];
  const hasKey = new Set();
  const registerKey = key => {
    if (!hasKey.has(key)) {
      hasKey.add(key);
      keys.push(key);
    }
  };

  const pools = new Map();
  const addToPool = item => {
    registerKey(item.key);
    if (!pools.has(item.key)) {
      pools.set(item.key, []);
    }
    pools.get(item.key).push(item);
  };

  downloadQueue.forEach(item => addToPool(item));
  addingQueue.forEach(series => {
    series.imageIds.forEach(imageId => addToPool(toDownloadItem(series, imageId)));
  });

  const nextQueue = [];
  let hasPending = true;
  while (hasPending) {
    hasPending = false;
    keys.forEach(key => {
      const pool = pools.get(key);
      if (pool && pool.length) {
        hasPending = true;
        nextQueue.push(pool.shift());
      }
    });
  }

  return nextQueue;
};

const STRATEGIES = Object.freeze({
  [STRATEGY.CONCAT]: concatStrategy,
  [STRATEGY.ALTERNATE]: alternateStrategy
});

export class DownloadScheduler {
  /**
   * @param {string} strategy
   * @param {boolean} verbose
   */
  constructor(strategy = STRATEGY.CONCAT, verbose = false) {
    this.strategy = Object.prototype.hasOwnProperty.call(STRATEGIES, strategy)
      ? strategy
      : STRATEGY.CONCAT;
    this.verbose = !!verbose;
    this.downloadQueue = [];
    this.addingQueue = [];
    this.removingQueue = [];
    this.freeze = false;
    this.seriesData = {};
  }

  get isDownloading() {
    return (
      Object.values(this.seriesData).reduce(
        (acc, curr) => curr.isDownloading || acc,
        false
      ) && this.downloadQueue.length > 0
    );
  }

  /**
   * Adds a series into scheduler input queue.
   * @param {string|number} key
   * @param {string|number} seriesId
   * @param {string|number} studyId
   * @param {Array<string|number>} imageIds
   * @returns {boolean}
   */
  addSeries(key, seriesId, studyId, imageIds) {
    if (Object.prototype.hasOwnProperty.call(this.seriesData, key)) {
      console.warn(`Series ${key} is already in the download scheduler`);
      return false;
    }
    const normalizedImages = Array.isArray(imageIds) ? imageIds.slice() : [];
    this.addingQueue.push({
      key,
      seriesId,
      studyId,
      imageIds: normalizedImages
    });
    this.seriesData[key] = {
      numberOfImages: normalizedImages.length,
      isDownloading: false
    };
    this.reworkQueue();
    return true;
  }

  /**
   * Removes a series from scheduler queues and state.
   * @param {string|number} key
   * @returns {void}
   */
  removeSeries(key) {
    this.addingQueue = this.addingQueue.filter(item => item.key !== key);
    this.removingQueue.push(key);
    delete this.seriesData[key];
    this.reworkQueue();
  }

  /**
   * Returns remaining/initial counters for a series key.
   * @param {string|number} key
   * @returns {{remaining:number,initial:number}|null}
   */
  getStatus(key) {
    const remaining = this.downloadQueue.filter(item => item.key === key).length;
    const initial = this.seriesData[key]?.numberOfImages;
    return remaining ? { remaining, initial } : null;
  }

  /**
   * Returns status map for all tracked series.
   * @returns {Object}
   */
  getOverallStatus() {
    const keys = Object.keys(this.seriesData);
    return Object.fromEntries(keys.map(key => [key, this.getStatus(key)]));
  }

  /**
   * Returns pending image ids for a specific series key.
   * @param {string|number} key
   * @returns {Array<string|number>}
   */
  getSeriesPendingImageIds(key) {
    return this.downloadQueue
      .filter(item => item.key === key)
      .map(item => item.imageId);
  }

  /**
   * Extracts next N queue slots and updates downloading flags.
   * @param {number} slotDimension
   * @returns {Array|null}
   */
  getNextSlot(slotDimension) {
    if (this.freeze) {
      return null;
    }
    const size = Number.isFinite(slotDimension)
      ? Math.max(0, Math.floor(slotDimension))
      : 0;
    const nextSlot = this.downloadQueue.splice(0, size);
    this.updateIsDownloading(nextSlot);
    if (this.verbose) {
      console.log("nextSlot", nextSlot);
    }
    return nextSlot;
  }

  /**
   * Reorders queued items of a series according to provided image rank.
   * @param {string|number} key
   * @param {Array<string|number>} orderedImageIds
   * @returns {boolean}
   */
  reprioritizeSeries(key, orderedImageIds = []) {
    if (
      !key ||
      !Array.isArray(orderedImageIds) ||
      orderedImageIds.length === 0 ||
      this.downloadQueue.length === 0
    ) {
      return false;
    }

    const queueIndexes = [];
    const seriesItems = [];
    this.downloadQueue.forEach((item, index) => {
      if (item.key === key) {
        queueIndexes.push(index);
        seriesItems.push(item);
      }
    });

    if (!seriesItems.length) {
      return false;
    }

    const rankByImageId = new Map();
    orderedImageIds.forEach((imageId, index) => {
      const imageKey = toImageKey(imageId);
      if (!rankByImageId.has(imageKey)) {
        rankByImageId.set(imageKey, index);
      }
    });
    if (!rankByImageId.size) {
      return false;
    }

    const fallbackRank = rankByImageId.size + 1;
    const reordered = seriesItems
      .map((item, originalIndex) => ({
        item,
        originalIndex,
        rank: rankByImageId.has(toImageKey(item.imageId))
          ? rankByImageId.get(toImageKey(item.imageId))
          : fallbackRank + originalIndex
      }))
      .sort((left, right) => {
        if (left.rank !== right.rank) {
          return left.rank - right.rank;
        }
        return left.originalIndex - right.originalIndex;
      })
      .map(entry => entry.item);

    let hasChanged = false;
    queueIndexes.forEach((queueIndex, index) => {
      if (this.downloadQueue[queueIndex] !== reordered[index]) {
        hasChanged = true;
      }
      this.downloadQueue[queueIndex] = reordered[index];
    });

    if (this.verbose && hasChanged) {
      console.log("reprioritizedQueue", key, this.getSeriesPendingImageIds(key));
    }

    return hasChanged;
  }

  /**
   * Rebuilds scheduler queue applying pending adds/removes and strategy.
   * @returns {void}
   */
  reworkQueue() {
    this.freeze = true;
    if (this.removingQueue.length) {
      const keysToRemove = new Set(this.removingQueue);
      this.downloadQueue = this.downloadQueue.filter(
        item => !keysToRemove.has(item.key)
      );
    }
    const strategyFn = STRATEGIES[this.strategy] || concatStrategy;
    this.downloadQueue = strategyFn(this.addingQueue, this.downloadQueue);
    this.addingQueue = [];
    this.removingQueue = [];
    if (this.verbose) {
      console.log("downloadQueue", this.downloadQueue);
    }
    this.freeze = false;
  }

  /**
   * Updates per-series downloading marker based on popped queue slot.
   * @param {Array} slot
   * @returns {void}
   */
  updateIsDownloading(slot) {
    // Use scheduler key consistently: callers may pass a DB series id
    // different from the queue key used by remove/reprioritize/status APIs.
    const seriesKeys = new Set((slot || []).map(item => item.key));
    seriesKeys.forEach(seriesKey => {
      const remaining = this.downloadQueue.filter(
        item => item.key === seriesKey
      ).length;
      if (remaining === 0) {
        delete this.seriesData[seriesKey];
        return;
      }
      if (this.seriesData[seriesKey]) {
        this.seriesData[seriesKey].isDownloading = true;
      }
    });
  }
}

export default DownloadScheduler;
export { STRATEGY as DOWNLOAD_SCHEDULER_STRATEGY };
